/**
 *  <gh-view> — read-only form renderer
 *
 *  Renders a view in display (form) mode. Use <gh-view-editor> for drag-and-drop editing.
 *
 *  ATTRIBUTES:
 *  'app-id'        - Application ID to load views from
 *  'view-id'       - ID of the specific view to render; renders first view if omitted
 *  'item-id'       - Item ID passed down to each gh-element
 *  'decorator'     - Two-way binding to a view model object (overrides app-id/view-id lookup)
 *  'fields'        - Field values map passed to gh-element (needed for add_item_action)
 *  'src-for-element' - Rendering source hint passed to gh-element (defaults to 'form')
 *
 *  VIEW MODEL SHAPE ('model' / 'decorator'):
 *      'template_id' - Template ID for block identification in 'views_list'
 *      'name'        - Display name of the container
 *      'icon_id'     - Icon identifier for the container
 *      'class'       - CSS class(es) applied to the container
 *      'priority'    - Sort order within parent containers
 *      'content'     - Array of child container/element descriptors
 *
 *  CONTAINER TEMPLATES (defined in views/form/, each exports { template_id, html_form }):
 *      'html_form'   - HTML template used to render each container in form mode
 **/

import { contextMenuHandler } from './gh_view_context_menu.js';
import * as Flex from './views/flex_form.js';
import * as Float from './views/float_form.js';
import * as Tabs from './views/tabs_form.js';
import * as Dropdown from './views/dropdown_form.js';

let FormTemplates = [Flex, Float, Tabs, Dropdown];

angular.module('GhTemplateModule', ['GhTemplateEditorModule'])

.directive("ghView", ['$animate', '$compile', '$filter', function($animate, $compile, $filter) {
    let directive = {
        template: '<div class="ghViewContainer ghForm"><gh-loader type="ai" color="blue"></gh-loader></div>',
        replace: false,
        restrict: 'E',
        controller: controller,
        scope: {
            appId: '@',
            viewId: '@',
            itemId: '@',
            decorator: '=?',
            fields: '=', // Need for add_item_action
            srcForElement: '@'
        },
        link: {
            post: function(scope, element) {
                let scopes = [];
                function buildTemplate(arr) {
                    let temp = '';
                    let sort = $filter('orderBy')(arr, 'priority');

                    angular.forEach(sort, function(value) {
                        if (value['template_id'] == 'gh_element') {
                            temp += `<gh-element app-id="${ scope.appId || '' }" item-id="${ scope.itemId || '' }" field-id="${ value.element_id || '' }" ${ scope.fields ? 'value="fields[' + value.element_id + ']"' : '' } container-id="${ value.container_id }" elem-src="${ scope.srcForElement }"></gh-element>`;
                        } else {
                            angular.forEach(scope.templates, function(template) {
                                if (template.default.template_id == value['template_id']) temp += template.default.html(value, buildTemplate);
                            });
                        }
                    });

                    return temp;
                }

                scope.rendering = function(newVal) {
                    if (newVal && newVal['content'] && newVal['content'].length > 0) {
                        scopes = scopes.filter(scope => scope.$destroy(), false);
                        angular.element(element[0].querySelector(".ghViewContainer.ghForm")).empty();

                        let sort = $filter('orderBy')(newVal['content'], 'priority');

                        angular.forEach(sort, function(value) {

                            angular.forEach(scope.templates, function(template) {

                                if (template.default.template_id == value['template_id']) {
                                    let template_string = template.default.html(value, buildTemplate);
                                    let linkFn = $compile(template_string);

                                    const newScope = scope.$new(false);
                                    scopes.push(newScope)
                                    angular.element(element[0].querySelector(".ghViewContainer.ghForm")).append(linkFn(newScope));
                                    newScope.$apply();
                                }

                            });

                        });
                    }
                };
            }
        }
    };

    controller.$inject = ['$scope', '$element', 'PipeService', 'cnfg', '$location', '$route', 'GhElementEditor', 'GhElementEditorLight', 'Message', 'formEditorService', 'localizationService'];

    async function controller($scope, $element, PipeService, cnfg, $location, $route, GhElementEditor, GhElementEditorLight, Message, formEditorService, localizationService) {
        if (!$scope.srcForElement) { $scope.srcForElement = 'form'; }

        $scope.templates = FormTemplates;

        $animate.enabled(false, $element);

        // Dropdown menus are position:fixed so they escape clipping by any
        // horizontally-scrolling ancestor; their on-screen coordinates have
        // to be computed in JS since CSS alone can't read an element's
        // own position relative to the viewport.
        $scope.positionDropdownMenu = function(event) {
            const trigger = event.currentTarget;
            const menu = trigger.querySelector('.dropdown_element_childs');
            if (!menu) { return; }

            const rect = trigger.getBoundingClientRect();
            menu.style.setProperty('--dropdown-menu-top', rect.bottom + 'px');
            menu.style.setProperty('--dropdown-menu-left', rect.left + 'px');

            // The menu's rows contain deeply nested widgets (gh-smart-input >
            // gh-button > ...) that defeat CSS shrink-to-fit sizing (width:
            // max-content resolves to something unrelated to their actual
            // content in this DOM). Measure each row's true content width
            // directly instead, and lock the menu to the widest one.
            const rows = menu.querySelectorAll('gh-element');
            let maxWidth = 0;
            rows.forEach(function(row) {
                row.style.setProperty('width', 'auto', 'important');
            });
            rows.forEach(function(row) {
                maxWidth = Math.max(maxWidth, row.scrollWidth);
            });
            rows.forEach(function(row) {
                row.style.removeProperty('width');
            });

            if (maxWidth > 0) {
                const menuStyle = window.getComputedStyle(menu);
                const horizontalPadding = parseFloat(menuStyle.paddingLeft) + parseFloat(menuStyle.paddingRight);
                menu.style.setProperty('--dropdown-menu-width', (maxWidth + horizontalPadding) + 'px');
            }
        };

        $scope.model = {};

        function update(newVal, int) {
            $scope.model = getView(newVal);
            console.log('---> NEW RENDERING! --- (' + int + ')');
            $scope.rendering($scope.model);
        }

        function getView(data) {
            let view = {};

            if ($scope.viewId) {
                angular.forEach(data, function(value) {
                    if (value.view_id == $scope.viewId) view = value;
                });
            } else {
                view = data[0];
            }

            return view;
        }

        let address = {
            app_id: $scope.appId
        };

        if ($scope.appId) {
            const views_list = await gudhub.appProcessor.getAppViews($scope.appId);
            update(views_list, 1);
            PipeService.on('gh_app_views_update', address, viewsUpdatePipe);
        }

        function viewsUpdatePipe(_event, data) {
            update(data, 2);
        }

        const handleContextMenuView = contextMenuHandler($scope, { cnfg, $location, $route, $element, GhElementEditor, GhElementEditorLight, Message, formEditorService, PipeService, localizationService });
        window.addEventListener('contextmenu', handleContextMenuView);

        $scope.$on('$destroy', function() {
            console.log('DESTROY');
            PipeService.destroy('gh_app_views_update', address, viewsUpdatePipe);
            window.removeEventListener('contextmenu', handleContextMenuView);
        });

        $scope.$watch('decorator', function(newDecorator) {
            if (newDecorator) {
                $scope.model = newDecorator;
                $scope.rendering($scope.model);
            }
        }, true);
    }

    return directive;
}]);
